import styles from './Tree.module.css';

import React, { memo, useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react-lite';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import classNames from 'classnames';
import TreeItem from './TreeItem';
import { FETCH_STATE } from '../../consts/storage.consts';

const StrictModeDroppable = ({ children, ...props }) => {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    const animation = requestAnimationFrame(() => setEnabled(true));
    return () => {
      cancelAnimationFrame(animation);
      setEnabled(false);
    };
  }, []);

  if (!enabled) {
    return null;
  }

  return (
    <Droppable {...props} isDropDisabled={props.disabledDrag}>
      {children}
    </Droppable>
  );
};

/**
 * Компонент древовидной структуры с возможностью перетаскивания элементов
 *
 * Компонент позволяет отображать иерархические данные в виде дерева с возможностью
 * разворачивания/сворачивания узлов, ленивой загрузки дочерних элементов,
 * перетаскивания элементов внутри дерева.
 *
 * @component
 * @param {Object} props - Свойства компонента
 * @param {Object} props.store - Хранилище данных, которое должно содержать методы для работы с деревом (loadItems, toggleNode, setSelectedNode и т.д.)
 * @param {Function} props.handlerLoadData - Функция для загрузки начальных данных
 * @param {Function} [props.onNodeClick] - Обработчик клика по узлу дерева
 * @param {Function} [props.loadChildren] - Функция для ленивой загрузки дочерних элементов. Принимает (nodeId, item) и должна возвращать Promise с массивом дочерних элементов
 * @param {Function} [props.handlerNodeHasChildren=(item) => item.hasChildren] - Функция определения наличия дочерних элементов у узла
 * @param {string} [props.nodeKeyField='id'] - Имя поля, которое используется как уникальный идентификатор узла
 * @param {string} [props.nodeLabelField='name'] - Имя поля, которое используется для отображения названия узла
 * @param {(string|Function|Object)} [props.nodeKeyConfig=null] - Конфигурация для создания составного ключа узла
 * @param {Function} [props.renderItem] - Функция для кастомного рендеринга узла
 * @param {string} [props.className] - Дополнительные CSS-классы для контейнера дерева
 * @param {(string|React.ReactNode)} [props.defaultIcon] - Иконка по умолчанию для узлов
 * @param {number} [props.maxDepth=10] - Максимальная глубина отображения дерева
 * @param {(React.ReactNode|Function)} [props.actionButtons] - Кнопки действий для узла или функция, которая возвращает кнопки (принимает item)
 * @param {Function} [props.onDragEnd] - Обработчик завершения перетаскивания элемента
 *
 * @returns {React.ReactElement} Древовидная структура с возможностью перетаскивания
 *
 * @example
 * // Базовое использование
 * <Tree
 *   store={treeStore}
 *   handlerLoadData={fetchTreeData}
 *   onNodeClick={(node) => console.log('Clicked node:', node)}
 * />
 *
 * @example
 * // С ленивой загрузкой дочерних элементов
 * <Tree
 *   store={treeStore}
 *   handlerLoadData={fetchRootNodes}
 *   loadChildren={(nodeId, item) => fetchChildrenForNode(nodeId)}
 *   handlerNodeHasChildren={(item) => item.hasSubItems}
 * />
 *
 * @example
 * // С кастомным рендерингом узлов
 * <Tree
 *   store={treeStore}
 *   handlerLoadData={fetchTreeData}
 *   renderItem={({ item, isExpanded, hasChildren, onToggle }) => (
 *     <div style={{ display: 'flex', alignItems: 'center' }}>
 *       {hasChildren && (
 *         <button onClick={onToggle}>{isExpanded ? '▼' : '►'}</button>
 *       )}
 *       <span>{item.title}</span>
 *     </div>
 *   )}
 * />
 *
 * @example
 * // С составным ключом узла
 * <Tree
 *   store={treeStore}
 *   handlerLoadData={fetchTreeData}
 *   nodeKeyConfig={{
 *     fields: ['id', 'type'],
 *     separator: '_'
 *   }}
 * />
 *
 * @example
 * // С кнопками действий
 * <Tree
 *   store={treeStore}
 *   handlerLoadData={fetchTreeData}
 *   actionButtons={(item) => (
 *     <div>
 *       <button onClick={(e) => { e.stopPropagation(); editItem(item); }}>
 *         Редактировать
 *       </button>
 *       <button onClick={(e) => { e.stopPropagation(); deleteItem(item); }}>
 *         Удалить
 *       </button>
 *     </div>
 *   )}
 * />
 */

const Tree = observer(
  ({
    store,
    handlerLoadData,
    onNodeClick,
    loadChildren,
    handlerNodeHasChildren = (item) => item.hasChildren,
    nodeKeyField = 'id',
    nodeLabelField = 'name',
    nodeKeyConfig = null,
    renderItem,
    className,
    defaultIcon,
    maxDepth = 10,
    actionButtons,
    onDragEnd,
    disabledDrag = true,
    filterFunction = undefined,
  }) => {
    const { items, fetchState, selectedNode, queryFilter } = store;

    const [loadingNodes, setLoadingNodes] = useState(new Set());

    useEffect(() => {
      store.loadItems(handlerLoadData);
    }, []);

    const getNodeId = useCallback(
      (item) => {
        if (!nodeKeyConfig || typeof nodeKeyConfig === 'string') {
          return item[nodeKeyConfig || nodeKeyField];
        }
        if (typeof nodeKeyConfig === 'function') {
          return nodeKeyConfig(item);
        }
        if (typeof nodeKeyConfig === 'object' && Array.isArray(nodeKeyConfig.fields)) {
          return nodeKeyConfig.fields
            .map((field) => item[field])
            .filter(Boolean)
            .join(nodeKeyConfig.separator || '_');
        }
        return item[nodeKeyField];
      },
      [nodeKeyConfig, nodeKeyField],
    );

    const safeGetChildren = useCallback((item) => {
      return item?.children || [];
    }, []);

    const hasNodeChildren = useCallback(
      (item) => item && handlerNodeHasChildren(item),
      [handlerNodeHasChildren],
    );

    const handleToggle = useCallback(
      async (nodeId, item) => {
        if (!item || !store) return;

        if (store.childrenMap[nodeId]?.length === 0) return;

        store.toggleNode(nodeId);

        if (store.expandedNodes.includes(nodeId) && loadChildren && !store.childrenMap[nodeId]) {
          try {
            setLoadingNodes((prev) => new Set([...prev, nodeId]));
            const children = await loadChildren(item.id, item, queryFilter);
            store.addChildren(nodeId, Array.isArray(children) ? children : []);
            if (!children?.length) store.toggleNode(nodeId);
          } catch (error) {
            console.error(`Ошибка загрузки для узла ${nodeId}:`, error);
            store.addChildren(nodeId, []);
          } finally {
            setLoadingNodes((prev) => {
              const newSet = new Set([...prev]);
              newSet.delete(nodeId);
              return newSet;
            });
          }
        }
      },
      [store, loadChildren, queryFilter],
    );

    const handleNodeClick = useCallback(
      (item) => {
        store?.setSelectedNode(item);
        onNodeClick?.(item);
      },
      [onNodeClick, store],
    );

    const handleDragEnd = useCallback(
      (result) => {
        const { source, destination } = result;
        if (!destination) return;

        if (source.droppableId === destination.droppableId && source.index === destination.index)
          return;

        store.moveItem(
          source.draggableId,
          destination.index,
          source.droppableId === 'root' ? null : source.droppableId,
          destination.droppableId === 'root' ? null : destination.droppableId,
        );

        if (onDragEnd) {
          onDragEnd(result);
        }
      },
      [store, onDragEnd],
    );

    const renderFirstLevel = useCallback(() => {
      let paddingLeft = 0;
      return (
        <StrictModeDroppable droppableId="root" type="root" disabledDrag={disabledDrag}>
          {(provided, snapshot) => (
            <div
              className={classNames(styles.list, {
                [styles.isDraggingOver]: snapshot.isDraggingOver,
              })}
              role="tree"
              ref={provided.innerRef}
              {...provided.droppableProps}
            >
              {items
                ?.filter((item) => !filterFunction || filterFunction(item))
                .map((item, index) => {
                  if (!item) return null;

                  const nodeId = getNodeId(item);
                  if (!nodeId) return null;

                  const isExpanded = store?.expandedNodes.includes(nodeId) || false;
                  const isLoading = loadingNodes.has(nodeId);
                  const hasChildren =
                    store.childrenMap[nodeId] !== undefined
                      ? hasNodeChildren(item) && store.childrenMap[nodeId]?.length > 0
                      : hasNodeChildren(item);
                  const isActive =
                    item?.id === selectedNode?.id && item?.entity === selectedNode?.entity;

                  return (
                    <Draggable
                      key={nodeId}
                      draggableId={String(nodeId)}
                      index={index}
                      isDragDisabled={disabledDrag}
                    >
                      {(dragProvided, dragSnapshot) => (
                        <div
                          className={classNames(styles.item, {
                            [styles.isDragging]: dragSnapshot.isDragging,
                          })}
                          role="treeitem"
                          aria-expanded={hasChildren ? isExpanded : undefined}
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                        >
                          <TreeItem
                            item={item}
                            isActive={isActive}
                            isExpanded={isExpanded}
                            isLoading={isLoading}
                            hasChildren={hasChildren}
                            onToggle={() => handleToggle(nodeId, item)}
                            onClick={() => handleNodeClick(item)}
                            renderItem={renderItem}
                            nodeLabelField={nodeLabelField}
                            defaultIcon={defaultIcon}
                            actionButtons={actionButtons}
                            dragHandleProps={dragProvided.dragHandleProps}
                            isDragging={dragSnapshot.isDragging}
                            paddingLeft={paddingLeft}
                          />

                          {renderSubLevel(nodeId, item, 1)}
                        </div>
                      )}
                    </Draggable>
                  );
                })}
              {provided.placeholder}
            </div>
          )}
        </StrictModeDroppable>
      );
    }, [items, store?.expandedNodes, loadingNodes, handleToggle, handleNodeClick, disabledDrag]);

    const renderSubLevel = useCallback(
      (parentId, parentItem, level) => {
        if (!store || level > maxDepth || !store.expandedNodes.includes(parentId)) {
          return null;
        }

        const children = store.childrenMap[parentId] || safeGetChildren(parentItem);
        if (!children?.length) return null;

        return (
          <StrictModeDroppable
            droppableId={String(parentId)}
            type="root"
            disabledDrag={disabledDrag}
          >
            {(provided, snapshot) => (
              <div role="group">
                <div
                  className={classNames(styles.list, {
                    [styles.isDraggingOver]: snapshot.isDraggingOver,
                  })}
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                >
                  {children
                    ?.filter((item) => !filterFunction || filterFunction(item))
                    .map((item, index) => {
                      if (!item) return null;

                      const nodeId = getNodeId(item);
                      if (!nodeId) return null;

                      const isExpanded = store.expandedNodes.includes(nodeId);
                      const isLoading = loadingNodes.has(nodeId);

                      const hasChildren =
                        store.childrenMap[nodeId] !== undefined
                          ? hasNodeChildren(item) && store.childrenMap[nodeId]?.length > 0
                          : hasNodeChildren(item);
                      const isActive =
                        item?.id === selectedNode?.id && item?.entity === selectedNode?.entity;

                      return (
                        <Draggable
                          key={nodeId}
                          draggableId={String(nodeId)}
                          index={index}
                          isDragDisabled={disabledDrag}
                        >
                          {(dragProvided, dragSnapshot) => (
                            <div
                              className={classNames(styles.item, {
                                [styles.isDragging]: dragSnapshot.isDragging,
                              })}
                              role="treeitem"
                              aria-expanded={hasChildren ? isExpanded : undefined}
                              ref={dragProvided.innerRef}
                              {...dragProvided.draggableProps}
                            >
                              <TreeItem
                                isActive={isActive}
                                item={item}
                                isExpanded={isExpanded}
                                isLoading={isLoading}
                                hasChildren={hasChildren}
                                onToggle={() => handleToggle(nodeId, item)}
                                onClick={() => handleNodeClick(item)}
                                renderItem={renderItem}
                                nodeLabelField={nodeLabelField}
                                defaultIcon={defaultIcon}
                                actionButtons={actionButtons}
                                dragHandleProps={dragProvided.dragHandleProps}
                                isDragging={dragSnapshot.isDragging}
                                paddingLeft={20 * level}
                              />

                              {renderSubLevel(nodeId, item, level + 1)}
                            </div>
                          )}
                        </Draggable>
                      );
                    })}
                  {provided.placeholder}
                </div>
              </div>
            )}
          </StrictModeDroppable>
        );
      },
      [store, loadingNodes, handleToggle, handleNodeClick, maxDepth, disabledDrag],
    );

    if (!items?.length && fetchState === FETCH_STATE.SUCCESS) {
      return <div className={styles.emptyState}>Нет данных для отображения</div>;
    }

    return (
      <DragDropContext onDragEnd={handleDragEnd}>
        <div
          className={classNames(styles.container, className)}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              handleNodeClick(null);
            }
          }}
        >
          {renderFirstLevel()}
        </div>
      </DragDropContext>
    );
  },
);

Tree.propTypes = {
  store: PropTypes.shape({
    items: PropTypes.array,
    fetchState: PropTypes.string,
    loadItems: PropTypes.func,
    toggleNode: PropTypes.func,
    expandedNodes: PropTypes.array,
    childrenMap: PropTypes.object,
    setSelectedNode: PropTypes.func,
    addChildren: PropTypes.func,
    moveItem: PropTypes.func,
  }).isRequired,
  handlerLoadData: PropTypes.func.isRequired,
  onNodeClick: PropTypes.func,
  loadChildren: PropTypes.func,
  handlerNodeHasChildren: PropTypes.func,
  nodeKeyField: PropTypes.string,
  nodeLabelField: PropTypes.string,
  nodeKeyConfig: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func,
    PropTypes.shape({
      fields: PropTypes.arrayOf(PropTypes.string).isRequired,
      separator: PropTypes.string,
    }),
  ]),
  renderItem: PropTypes.func,
  className: PropTypes.string,
  defaultIcon: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  maxDepth: PropTypes.number,
  actionButtons: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  onDragEnd: PropTypes.func,
};

export default memo(Tree);
